import { toast } from '../../../common/toast';
import { axiosInstanceNoToast } from '../../../fetchConfig/axiosConfig.js';
import logger from '../../../utils/logger';
import { usePersistedDataStore } from '../../store.js';
import { groupSettledRowsByConsecutiveStage } from './settlementStageUtils.js';

export const DOWNLOAD_STIPS_OPTION = 'Download_Stips';

const LOG_PREFIX = '[DownloadStips]';

/**
 * Payload for `/generate_stipulations` (matches settlement batch shape).
 */
export const buildStipulationObject = ({ stage = 'SCAR', villageRun = 0, taxYear }) => ({
  isBar: stage === 'BAR',
  village: villageRun === 1,
  year: taxYear,
});

const fetchPdfBytes = async (url) => {
  const res = await fetch(url, { method: 'GET', mode: 'cors', credentials: 'omit' });
  if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
  return new Uint8Array(await res.arrayBuffer());
};

const mergeStipulationPdfUrls = async (urls) => {
  const { PDFDocument } = await import('pdf-lib');
  const mergedPdf = await PDFDocument.create();

  for (const url of urls) {
    const pdfBytes = await fetchPdfBytes(url);
    const pdf = await PDFDocument.load(pdfBytes);
    const pages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
    pages.forEach((page) => mergedPdf.addPage(page));
  }

  return mergedPdf.save();
};

const downloadMergedPdfBytes = async (pdfBytes, filename) => {
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
};

const uploadMergedStipulationBlob = async (blob, downloadFileName) => {
  const { uploadFileToS3Presigned } = await import('../Offers/s3UploadService.js');
  const s3Key = `Stipulations/stipulations_${Date.now()}.pdf`;
  return uploadFileToS3Presigned(blob, s3Key, 'negotiation-files', `${downloadFileName}.pdf`);
};

async function downloadCountersignedCopies({ settledRows, year, village, showToasts }) {
  const parcelIds = [...new Set(settledRows.map((row) => row.parcel_id).filter(Boolean))];
  const response = await axiosInstanceNoToast.post('/countersigned-stipulations', {
    parcelIds,
    year,
    village,
  });
  if (!response.data?.success || !response.data.pdfBase64) {
    return {
      success: false,
      error: response.data?.error || 'No uploaded countersigned copies for these parcels.',
    };
  }

  const binary = atob(response.data.pdfBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  await downloadMergedPdfBytes(bytes, 'Countersigned_Stipulations.pdf');

  const missingCount = response.data.missingParcelIds?.length || 0;
  if (missingCount && showToasts) {
    toast.warning(
      `No uploaded countersigned copy for ${missingCount} parcel${missingCount === 1 ? '' : 's'}.`,
      { autoClose: false },
    );
  }
  return { success: true, message: 'Countersigned stipulations downloaded' };
}

const downloadFile = async (url, filename = null) => {
  logger.log('📥 [STIPULATIONS] Attempting to download file:', { url, filename });

  try {
    const res = await fetch(url, { method: 'GET', mode: 'cors', credentials: 'omit' });
    if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);

    const blob = await res.blob();

    let downloadFilename = filename;

    if (!downloadFilename) {
      const contentDisposition = res.headers.get('Content-Disposition');
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          downloadFilename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
    }

    if (!downloadFilename) {
      const urlParts = url.split('/');
      const s3Filename = urlParts[urlParts.length - 1].split('?')[0];
      downloadFilename = s3Filename || 'document.pdf';
    }

    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = downloadFilename;
    link.style.display = 'none';
    link.setAttribute('download', downloadFilename);

    document.body.appendChild(link);

    try {
      link.click();
      setTimeout(() => {
        try {
          if (link.parentNode) {
            document.body.removeChild(link);
          }
        } catch (e) {
          // Ignore cleanup errors
        }
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      }, 100);
    } catch (clickError) {
      logger.error('❌ [STIPULATIONS] Click failed, trying fallback:', clickError);
      try {
        if (link.parentNode) {
          document.body.removeChild(link);
        }
      } catch (e) {
        // Ignore cleanup errors
      }
      const w = window.open(blobUrl, '_blank');
      if (!w) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    }
  } catch (err) {
    logger.error('❌ [STIPULATIONS] Failed to download file:', err);
    try {
      const w = window.open(url, '_blank', 'noopener,noreferrer');
      if (!w) {
        logger.info('💡 [STIPULATIONS] Please download the file manually from:', url);
      }
    } catch (fallbackError) {
      logger.error('❌ [STIPULATIONS] All download methods failed:', fallbackError);
    }
  }
};

/**
 * Build stipulation payload, call `/generate_stipulations`, optionally download the file.
 * @param {{ parcelIds: string[], stipulation: object, shouldDownload?: boolean }} params
 */
export const generateStipulations = async ({ parcelIds, stipulation, shouldDownload = false }) => {
  if (!parcelIds?.length) {
    return { success: false, error: 'No parcel IDs provided' };
  }
  if (!stipulation || typeof stipulation !== 'object') {
    return { success: false, error: 'stipulation object is required' };
  }

  try {
    logger.log('🔧 [STIPULATIONS] Generating stipulations for', parcelIds.length, 'parcels');
    const response = await axiosInstanceNoToast.post('/generate_stipulations', {
      parcelIds,
      stipulation,
    });

    if (!response.data?.success) {
      const error = response.data?.error || 'Stipulation generation failed';
      logger.error('❌ [STIPULATIONS] Generation failed:', error);
      return { success: false, error };
    }

    const result = {
      success: true,
      file_url: response.data.file_url,
      message: response.data.message,
    };
    logger.log('✅ [STIPULATIONS] Generated successfully:', result);

    if (shouldDownload && result.file_url) {
      await downloadFile(result.file_url);
    } else if (shouldDownload && !result.file_url) {
      logger.warn('⚠️ [STIPULATIONS] Download requested but no file_url in response');
    }

    return result;
  } catch (error) {
    logger.error('❌ [STIPULATIONS] Error generating stipulations:', error);
    return {
      success: false,
      error: error?.response?.data?.error || error.message || 'Unknown error occurred',
    };
  }
};

/**
 * Generate stipulations per stage group (preserving row order), merging when needed.
 * @param {{ settledRows: object[], negotiationObj: object, shouldDownload?: boolean, downloadFileName?: string }} params
 */
export async function generateStipulationsForSettledRows({
  settledRows,
  negotiationObj,
  shouldDownload = false,
  downloadFileName = 'Stipulations',
}) {
  const villageRun = negotiationObj.villageRun ?? 0;
  const taxYear = negotiationObj.TaxYear;
  const groups = groupSettledRowsByConsecutiveStage(settledRows, negotiationObj).filter(
    ({ stage }) => stage !== 'Art7'
  );
  if (groups.length === 0) {
    return { success: false, error: 'Art 7 settlements do not generate BAR/SCAR stipulations.' };
  }

  if (groups.length > 1) {
    logger.log(`${LOG_PREFIX} Mixed stages detected; generating ${groups.length} stip batches in row order`);
  }

  const fileUrls = [];
  for (const { stage, rows } of groups) {
    const parcelIds = [...new Set(rows.map((row) => row.parcel_id).filter(Boolean))];
    const stipulation = buildStipulationObject({ stage, villageRun, taxYear });
    const result = await generateStipulations({ parcelIds, stipulation, shouldDownload: false });
    if (!result.success) return result;
    if (!result.file_url) {
      return { success: false, error: 'Stipulation generation returned no file URL' };
    }
    fileUrls.push(result.file_url);
  }

  if (fileUrls.length === 1) {
    const file_url = fileUrls[0];
    if (shouldDownload) await downloadFile(file_url);
    return { success: true, file_url, message: 'Stipulations generated successfully' };
  }

  const mergedBytes = await mergeStipulationPdfUrls(fileUrls);
  const blob = new Blob([mergedBytes], { type: 'application/pdf' });
  const mergedFileName = `${downloadFileName}.pdf`;

  if (shouldDownload) {
    await downloadMergedPdfBytes(mergedBytes, mergedFileName);
    return { success: true, message: 'Stipulations generated successfully' };
  }

  const file_url = await uploadMergedStipulationBlob(blob, downloadFileName);
  return { success: true, file_url, message: 'Stipulations generated successfully' };
}

/** Generate stips after settlement; downloads only when the user enabled "Download Stips". */
export const generateStipsOnSettlement = async ({ parcelIds, stipulation }) => {
  const shouldDownload = usePersistedDataStore.getState().downloadStips === true;
  return generateStipulations({ parcelIds, stipulation, shouldDownload });
};

export function isCaseSettledForStipDownload(row) {
  return !!row && !row.loading && (row._isSettled || row.statusDisplay === 'Settled');
}

export function getSettledRowsForStipDownload({ rows, selectedRows }) {
  const sourceRows = selectedRows?.length > 0 ? selectedRows : rows;
  if (!sourceRows?.length) return [];
  return sourceRows.filter(isCaseSettledForStipDownload);
}

/**
 * Generate stipulations for settled grid rows; downloads by default (toolbar / download menu).
 * @param {{ rows: object[], selectedRows?: object[], shouldDownload?: boolean, showToasts?: boolean, countersignedOnly?: boolean }} params
 */
export async function downloadStips({ rows, selectedRows, shouldDownload = true, showToasts = true, countersignedOnly = false }) {
  const negotiationObj = usePersistedDataStore.getState().negotiationObj;
  if (!negotiationObj?.TaxYear) {
    const error = 'Missing municipality or tax year.';
    if (showToasts) toast.error(error, { autoClose: false });
    return { success: false, error };
  }

  const settledRows = getSettledRowsForStipDownload({ rows, selectedRows });

  if (settledRows.length === 0) {
    const error = 'No settled cases to download stipulations for.';
    if (showToasts) toast.error(error, { autoClose: false });
    return { success: false, error };
  }

  const setStipsDownloadInFlight = usePersistedDataStore.getState().setStipsDownloadInFlight;
  setStipsDownloadInFlight(true);
  try {
    const result = countersignedOnly
      ? await downloadCountersignedCopies({
          settledRows,
          year: negotiationObj.TaxYear,
          village: negotiationObj.villageRun === 1,
          showToasts,
        })
      : await generateStipulationsForSettledRows({
          settledRows,
          negotiationObj,
          shouldDownload,
        });
    if (!result.success && showToasts) {
      toast.error(result.error || 'Failed to generate stipulations', { autoClose: false });
    }
    return result;
  } catch (error) {
    logger.error(`${LOG_PREFIX} Failed:`, error);
    const message = error?.response?.data?.error || error.message || 'Failed to generate stipulations';
    if (showToasts) toast.error(message, { autoClose: false });
    return { success: false, error: message };
  } finally {
    setStipsDownloadInFlight(false);
  }
}
